import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle2,
  X,
  Loader2,
  Package,
  Layers,
  Info,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { adminAPI, productAPI } from '../../../services/api';
import { downloadClientSampleTemplate } from '../../../utils/orderImportTemplate';

export interface ImportOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrdersImported: () => void;
}

interface ParsedOrderRow {
  rowNumber: number;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  shippingAddress: string;
  productIdentifier: string;
  quantity: number;
  amount: number | string;
  shippingCost: number | string;
  color: string;
  size: string;
  weight: string;
  note: string;
  isValid: boolean;
  validationError?: string;
}

const COLUMN_ALIASES: Record<string, string[]> = {
  orderNumber: ['order number', 'order #', 'order_number', 'order_no', 'order no', 'invoice number', 'invoice #', 'invoice_number', 'invoice', 'order id', 'order_id'],
  customerName: ['customer name', 'customer_name', 'name', 'customer', 'client', 'buyer', 'recipient name', 'recipient_name', 'recipient', 'full name'],
  customerPhone: ['customer phone', 'customer_phone', 'phone', 'mobile', 'contact', 'phone number', 'phone_number', 'cell', 'mobile number', 'recipient phone', 'recipient_phone'],
  shippingAddress: ['shipping address', 'shipping_address', 'address', 'customer address', 'customer_address', 'delivery address', 'location', 'destination', 'recipient address'],
  productIdentifier: ['product name', 'product_name', 'product title', 'product_title', 'product', 'item', 'item name', 'item_name', 'title', 'sku', 'product id', 'item description'],
  quantity: ['quantity', 'qty', 'count', 'total lot', 'total_lot', 'lots', 'pieces'],
  amount: ['cod amount', 'cod_amount', 'cod', 'total amount', 'total_amount', 'total', 'amount', 'price', 'payable', 'final amount'],
  shippingCost: ['shipping cost', 'shipping_cost', 'shipping', 'delivery fee', 'delivery_fee', 'delivery charge'],
  color: ['color', 'colour', 'selected_color', 'selected color', 'variant color'],
  size: ['size', 'selected_size', 'selected size', 'variant size'],
  weight: ['weight', 'selected_weight', 'selected weight', 'variant weight'],
  note: ['note', 'notes', 'remarks', 'remark', 'comment', 'instructions', 'customer note', 'order note'],
};

const sanitizePhone = (raw: any): string => {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('880')) digits = digits.slice(2);
  else if (digits.startsWith('88') && digits.length > 11) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith('1')) digits = '0' + digits;
  else if (digits.length === 9 && /^[3-9]/.test(digits)) digits = '01' + digits;
  return digits;
};

const parseMohammadParcelNote = (rawNote: string) => {
  const res = { color: '', size: '', weight: '', note: '' };
  if (!rawNote) return res;
  const mColor = rawNote.match(/(?:^|[,|])\s*colou?r\s*[:=]\s*([^,|]+)/i);
  if (mColor) res.color = mColor[1].trim();
  const mSize = rawNote.match(/(?:^|[,|])\s*size\s*[:=]\s*([^,|]+)/i);
  if (mSize) res.size = mSize[1].trim();
  const mWeight = rawNote.match(/(?:^|[,|])\s*weight\s*[:=]\s*([^,|]+)/i);
  if (mWeight) res.weight = mWeight[1].trim();
  const mNote = rawNote.match(/(?:^|[,|])\s*notes?\s*[:=]\s*(.+)$/i);
  if (mNote) {
    res.note = mNote[1].trim();
  } else {
    let clean = rawNote;
    if (res.color) clean = clean.replace(/(?:^|[,|])\s*colou?r\s*[:=]\s*[^,|]+/i, '');
    if (res.size) clean = clean.replace(/(?:^|[,|])\s*size\s*[:=]\s*[^,|]+/i, '');
    if (res.weight) clean = clean.replace(/(?:^|[,|])\s*weight\s*[:=]\s*[^,|]+/i, '');
    clean = clean.replace(/^[\s,|]+|[\s,|]+$/g, '').trim();
    res.note = clean;
  }
  return res;
};

export const ImportOrdersModal: React.FC<ImportOrdersModalProps> = ({
  isOpen,
  onClose,
  onOrdersImported,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedOrderRow[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  // Import options
  const [defaultProductId, setDefaultProductId] = useState<string>('');
  const [defaultStatus, setDefaultStatus] = useState<string>('pending');
  const [defaultTrafficSource, setDefaultTrafficSource] = useState<string>('manual');
  const [defaultShippingCost, setDefaultShippingCost] = useState<string>('');
  const [skipDuplicates, setSkipDuplicates] = useState<boolean>(true);
  const [deductStock, setDeductStock] = useState<boolean>(true);

  // Status & Results
  const [generalError, setGeneralError] = useState<string>('');
  const [importResult, setImportResult] = useState<any | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load products list for fallback dropdown
  useEffect(() => {
    if (isOpen) {
      productAPI.getProducts({ page_size: 100 })
        .then((res: any) => {
          const prods = res.data?.results || res.data || [];
          setProducts(prods);
        })
        .catch(() => {});
    } else {
      // Reset state on close
      setFile(null);
      setParsedRows([]);
      setGeneralError('');
      setImportResult(null);
      setImporting(false);
      setParsing(false);
    }
  }, [isOpen]);

  const processFile = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setParsing(true);
    setGeneralError('');
    setImportResult(null);

    try {
      const buffer = await selectedFile.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = wb.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('Spreadsheet has no worksheets.');
      }

      const ws = wb.Sheets[firstSheetName];
      const rawRows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!rawRows || rawRows.length < 2) {
        throw new Error('Spreadsheet does not contain data rows.');
      }

      const headerRow = rawRows[0].map(h => String(h || '').trim().toLowerCase().replace(/[\s_-]+/g, ' '));
      const colMap: Record<string, number> = {};

      Object.entries(COLUMN_ALIASES).forEach(([canonical, aliases]) => {
        for (let i = 0; i < headerRow.length; i++) {
          const h = headerRow[i];
          if (!h) continue;
          if (aliases.some(a => h === a || h.startsWith(`${a} `) || h.endsWith(` ${a}`))) {
            colMap[canonical] = i;
            break;
          }
        }
      });

      if (!('customerPhone' in colMap) && !('shippingAddress' in colMap) && !('customerName' in colMap)) {
        throw new Error('Could not identify essential customer columns. Please download the sample template.');
      }

      const processed: ParsedOrderRow[] = [];

      for (let r = 1; r < rawRows.length; r++) {
        const row = rawRows[r];
        if (!row || row.every(c => c === '' || c === null || c === undefined)) continue;

        const getCell = (key: string) => {
          const idx = colMap[key];
          return idx !== undefined && idx < row.length ? String(row[idx] ?? '').trim() : '';
        };

        const rawNote = getCell('note');
        const parsedNote = parseMohammadParcelNote(rawNote);

        const custPhone = sanitizePhone(getCell('customerPhone'));
        const custAddress = getCell('shippingAddress');
        const custName = getCell('customerName') || 'Customer';

        let isValid = true;
        let validationError = '';

        if (!custPhone || custPhone.length < 10) {
          isValid = false;
          validationError = 'Invalid phone number';
        } else if (!custAddress) {
          isValid = false;
          validationError = 'Missing shipping address';
        }

        processed.push({
          rowNumber: r + 1,
          orderNumber: getCell('orderNumber'),
          customerName: custName,
          customerPhone: custPhone,
          shippingAddress: custAddress,
          productIdentifier: getCell('productIdentifier'),
          quantity: Math.max(1, parseInt(getCell('quantity') || '1', 10) || 1),
          amount: getCell('amount'),
          shippingCost: getCell('shippingCost'),
          color: getCell('color') || parsedNote.color,
          size: getCell('size') || parsedNote.size,
          weight: getCell('weight') || parsedNote.weight,
          note: parsedNote.note || rawNote,
          isValid,
          validationError,
        });
      }

      setParsedRows(processed);
    } catch (err: any) {
      setGeneralError(err.message || 'Failed to read file. Please ensure it is a valid .xlsx or .csv.');
      setParsedRows([]);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleExecuteImport = async () => {
    if (!file && parsedRows.length === 0) return;
    setImporting(true);
    setGeneralError('');

    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (defaultProductId) formData.append('default_product_id', defaultProductId);
      formData.append('default_status', defaultStatus);
      formData.append('default_traffic_source', defaultTrafficSource);
      if (defaultShippingCost) formData.append('default_shipping_cost', defaultShippingCost);
      formData.append('skip_duplicates', skipDuplicates ? 'true' : 'false');
      formData.append('deduct_stock', deductStock ? 'true' : 'false');

      const res: any = await adminAPI.bulkImportOrders(formData);
      setImportResult(res.data || res);
      onOrdersImported();
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to import orders.';
      setGeneralError(errMsg);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const validCount = parsedRows.filter(r => r.isValid).length;
  const errorCount = parsedRows.filter(r => !r.isValid).length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(3px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !importing) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '14px',
          width: '100%',
          maxWidth: '840px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          animation: 'omScaleIn 0.18s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(to right, #faf5ff, #fff)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: '#f3e8ff',
                color: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Upload size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
                Import Orders from Spreadsheet
              </h2>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                Upload .xlsx, .xls, or .csv files to create manual orders in bulk
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={importing}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Top Info & Template Download Box */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Info size={18} style={{ color: '#6366f1', flexShrink: 0 }} />
              <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                <strong>Need the format?</strong> Download sample templates with example rows:
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => downloadClientSampleTemplate('xlsx')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#15803d',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                <Download size={13} /> Excel (.xlsx)
              </button>
              <button
                type="button"
                onClick={() => downloadClientSampleTemplate('csv')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#0284c7',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                <Download size={13} /> CSV (.csv)
              </button>
            </div>
          </div>

          {/* Error banner */}
          {generalError && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#dc2626',
                fontSize: '0.84rem',
              }}
            >
              <AlertCircle size={18} />
              <span>{generalError}</span>
            </div>
          )}

          {/* Results Summary Box if already imported */}
          {importResult && (
            <div
              style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '10px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontWeight: 700, fontSize: '0.95rem' }}>
                <CheckCircle2 size={20} />
                <span>Import Completed Successfully!</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                <div style={{ background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #dcfce7', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Imported</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#16a34a' }}>{importResult.imported_count || 0}</div>
                </div>
                <div style={{ background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Skipped (Dupes)</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#eab308' }}>{importResult.skipped_count || 0}</div>
                </div>
                <div style={{ background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase' }}>Errors</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444' }}>{importResult.error_count || 0}</div>
                </div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#b91c1c', marginBottom: '4px' }}>Row Errors:</div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.76rem', color: '#991b1b' }}>
                    {importResult.errors.slice(0, 5).map((e: any, i: number) => (
                      <li key={i}>Row {e.row}: {e.error} ({e.customer} / {e.phone})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Upload Area */}
          {!importResult && (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? '#7c3aed' : '#cbd5e1'}`,
                backgroundColor: isDragging ? '#faf5ff' : '#f8fafc',
                borderRadius: '12px',
                padding: '30px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#ede9fe',
                  color: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {parsing ? <Loader2 size={24} className="om-spin" /> : <FileSpreadsheet size={24} />}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: '#1e293b' }}>
                  {file ? file.name : 'Click to select or drag and drop your order file'}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                  Supports Microsoft Excel (.xlsx, .xls) and CSV (.csv)
                </p>
              </div>
              {file && (
                <span
                  style={{
                    backgroundColor: '#e0e7ff',
                    color: '#3730a3',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                  }}
                >
                  {(file.size / 1024).toFixed(1)} KB
                </span>
              )}
            </div>
          )}

          {/* Configuration Settings */}
          {!importResult && parsedRows.length > 0 && (
            <div
              style={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Layers size={15} style={{ color: '#7c3aed' }} /> Default Options & Settings
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                {/* Default Product */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                    Default Product (if not in sheet)
                  </label>
                  <select
                    value={defaultProductId}
                    onChange={(e) => setDefaultProductId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      backgroundColor: '#fff',
                      color: '#1e293b',
                    }}
                  >
                    <option value="">-- Auto-match from title / SKU --</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name_en || p.name_bn} (৳{p.discount_price || p.price})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Default Status */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                    Initial Order Status
                  </label>
                  <select
                    value={defaultStatus}
                    onChange={(e) => setDefaultStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      backgroundColor: '#fff',
                      color: '#1e293b',
                    }}
                  >
                    <option value="pending">Pending (New Orders)</option>
                    <option value="processing">Processing</option>
                    <option value="preorder">PreOrder</option>
                    <option value="shipped">Shipped</option>
                  </select>
                </div>

                {/* Traffic Source */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                    Order Traffic Source
                  </label>
                  <select
                    value={defaultTrafficSource}
                    onChange={(e) => setDefaultTrafficSource(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      backgroundColor: '#fff',
                      color: '#1e293b',
                    }}
                  >
                    <option value="manual">Manual (Call / Register)</option>
                    <option value="facebook">Facebook Ads / Sheet</option>
                    <option value="whatsapp">WhatsApp Order</option>
                    <option value="tiktok">TikTok Ads</option>
                    <option value="google">Google Ads</option>
                  </select>
                </div>

                {/* Flat Shipping Charge Override */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                    Delivery Fee Override (৳)
                  </label>
                  <input
                    type="number"
                    placeholder="Auto (৳60 Dhaka / ৳120 Outside)"
                    value={defaultShippingCost}
                    onChange={(e) => setDefaultShippingCost(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      backgroundColor: '#fff',
                      color: '#1e293b',
                    }}
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '6px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                  <span>Skip already existing order numbers</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={deductStock}
                    onChange={(e) => setDeductStock(e.target.checked)}
                  />
                  <span>Deduct product inventory stock</span>
                </label>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {!importResult && parsedRows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#334155' }}>
                  Preview Rows ({parsedRows.length} total)
                </div>
                <div style={{ display: 'flex', gap: '8px', fontSize: '0.75rem' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {validCount} valid</span>
                  {errorCount > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>⚠ {errorCount} errors</span>}
                </div>
              </div>

              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  maxHeight: '220px',
                  overflowY: 'auto',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: '#f1f5f9', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>#</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>Customer</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>Phone</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>Address</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>Item</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>COD (৳)</th>
                      <th style={{ padding: '8px 10px', color: '#475569', fontWeight: 600 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 10).map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: !row.isValid ? '#fef2f2' : idx % 2 === 0 ? '#fff' : '#f8fafc',
                        }}
                      >
                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{row.rowNumber}</td>
                        <td style={{ padding: '7px 10px', fontWeight: 600, color: '#1e293b' }}>{row.customerName}</td>
                        <td style={{ padding: '7px 10px', color: row.customerPhone ? '#1e293b' : '#dc2626' }}>
                          {row.customerPhone || 'Missing'}
                        </td>
                        <td
                          style={{
                            padding: '7px 10px',
                            color: '#475569',
                            maxWidth: '180px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.shippingAddress}
                        >
                          {row.shippingAddress || 'Missing address'}
                        </td>
                        <td
                          style={{
                            padding: '7px 10px',
                            color: '#475569',
                            maxWidth: '140px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.productIdentifier}
                        >
                          {row.productIdentifier || '(Default)'}
                          {row.color && ` · ${row.color}`}
                          {row.size && ` · ${row.size}`}
                        </td>
                        <td style={{ padding: '7px 10px', fontWeight: 600, color: '#0f172a' }}>
                          {row.amount ? `৳${row.amount}` : 'Auto'}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          {row.isValid ? (
                            <span style={{ color: '#16a34a', fontSize: '0.72rem', fontWeight: 600 }}>Ready</span>
                          ) : (
                            <span style={{ color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}>
                              {row.validationError}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedRows.length > 10 && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', textAlign: 'center' }}>
                  Showing first 10 of {parsedRows.length} rows
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            style={{
              backgroundColor: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '0.84rem',
              fontWeight: 600,
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            {importResult ? 'Close' : 'Cancel'}
          </button>

          {!importResult && (
            <button
              type="button"
              onClick={handleExecuteImport}
              disabled={importing || validCount === 0}
              style={{
                backgroundColor: validCount === 0 ? '#cbd5e1' : '#7c3aed',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 20px',
                fontSize: '0.86rem',
                fontWeight: 700,
                color: '#fff',
                cursor: validCount === 0 || importing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: validCount > 0 ? '0 4px 6px -1px rgba(124, 58, 237, 0.3)' : 'none',
                transition: 'background 0.15s',
              }}
            >
              {importing ? (
                <>
                  <Loader2 size={16} className="om-spin" /> Importing Orders...
                </>
              ) : (
                <>
                  <Package size={16} /> Import {validCount} Order{validCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}

          {importResult && (
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 22px',
                fontSize: '0.86rem',
                fontWeight: 700,
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

